import { z } from "zod";

// Base schemas for data validation
export const typeSchema = z.object({
  Name: z.string().min(1).max(200).transform(s => s.trim().replace(/\s+/g, ' ')),
  Version: z.number().int().min(0).default(0),
  SynchronizeWith: z.string().max(500).optional(),
  Usability: z.string().max(200).optional(),
  ApplicationID: z.number().int().min(0).default(0),
  CreateBlank: z.boolean().default(true),
  ExistingDataTypeLink: z.string().max(500).optional(),
  Creator: z.string().optional(),
  DisplaySequence: z.number().int().min(0).default(0),
  DataLabel: z.string().optional(),
  ID: z.number().int().min(0).default(0),
  CreatedDateTime: z.string().datetime().default(() => new Date().toISOString()),
});

export const ruleSchema = z.object({
  Name: z.string().min(1).max(200).transform((s) => s.trim().replace(/\s+/g, " ")),
  Body: z.string().optional(),
  Description: z.string().optional(),
  DataTypeName: z.string().max(500).nullable(),
  ObjectTypeFor: z.string().max(200).optional(),
  ApplicationID: z.number().int().min(0),
  Type: z.number().int().min(0),
  Status: z.number().int().min(0),
  Approved: z.number().int().min(0),
  Rule: z.string().max(50),
  RuleExpression: z.string().optional(),
  EvaluationStartMessage: z.string().optional(),
  EvaluationSuccessMessage: z.string().optional(),
  EvaluationFailMessage: z.string().optional(),
  PublishFlag: z.boolean().nullable(),
  RuleExpressionJson: z.string().nullable(),
  ID: z.number().int().min(0).optional(),
  Version: z.number().int().min(0),
  EffectiveDate: z.string().nullable(),
  CreatedBy: z.string().optional(),
  ModifiedBy: z.string().optional(),
  ModifiedDate: z.string().datetime(),
  StatusDate: z.string().nullable(),
  StatusDescription: z.string().nullable(),
  CreatedDate: z.string().datetime(),
  CRIndexID: z.number().int().min(0),
  Source: z.string().optional(),
  DisplaySequence: z.number().nullable(),
});

export const ruleExpressionSchema = z.object({
  FunctionName: z.string().min(1, "Function Name is required"),
  ParamCount: z.number().int().min(0),
  Parameters: z.string().nullable(),
  Approved: z.number().int().min(0),
  Modifiedby: z.string().nullable(),
  Description: z.string().optional(),
  Example: z.string().optional(),
  Configured: z.boolean(),
  Id: z.number().int().min(0),
  DateTimeCreated: z.string().datetime(),
  ModifiedDate: z.string().datetime(),
  params: z.array(
    z.object({
      ParamType: z.string().min(1, "Parameter type required"),
      ParamName: z.string().min(1, "Parameter name required"),
      Id: z.number(),
    })
  ).optional(),
});

export const insertRuleSchema = ruleSchema.pick({
  Name: true,
  Body: true,
  Description: true,
  DataTypeName: true,
  ObjectTypeFor: true,
  ApplicationID: true,
  Type: true,
  Status: true,
  Approved: true,
  Rule: true,
  RuleExpression: true,
  EvaluationStartMessage: true,
  EvaluationSuccessMessage: true,
  EvaluationFailMessage: true,
  PublishFlag: true,
  RuleExpressionJson: true,
  ID: true
})
  .extend({
    ExistingRuleID: z.number().optional(),
  });

export const ruleVersionSchema = z.object({
  Rule_Id: z.number().int().min(0),
  ObjectId: z.number().int().min(0),
  Version: z.number().int().min(0),
  DateTimeChanged: z.string().datetime(),
  Name: z.string().min(1),
  Type: z.number().int().min(0),
  ObjectName: z.string().max(200),
  ObjectVersionId: z.string().nullable(),
  raw_rule: z.string().max(200),
  rule_with_value: z.string().nullable(),
});

export const rulePublishSchema = z.object({
  Id: z.number().int().min(0),
  ObjectId: z.number().int().min(0),
  Version: z.number().int().min(0),
  Data: z.string(),
  DateTimeChanged: z.string().datetime(),
  Rule_Id: z.number().int().min(0),
  Remarks: z.string(),
  Approved: z.number().int().min(0),
  DisplaySequence: z.number().int().min(0),
  Name: z.string().min(1),
  Type: z.number().int().min(0),
});

const filteredRuleExpressionSchema = ruleExpressionSchema.pick({
  Id: true,
  FunctionName: true,
  Approved: true,
});

const filteredRuleSchema = ruleSchema.pick({
  ID: true,
  Name: true,
  Approved: true,
  DataTypeName: true,
  ApplicationID: true,
  Status: true,
});

export const insertRuleFunctionSchema = z.object({
  FunctionName: z.string().min(1, "Function Name is required"),
  Description: z.string().optional(),
  Example: z.string().optional(),
  params: z.array(
    z.object({
      ParamType: z.string().min(1, "Parameter type required"),
      ParamName: z.string().min(1, "Parameter name required"),
      Id: z.number(),
    })
  ).optional(),
});

export const codeSchema = z.object({
  Name: z.string().min(1).max(200).transform(s => s.trim().replace(/\s+/g, ' ')),
  type: z.string().max(500).optional(),
  use: z.string().optional(),
  label: z.string().max(500),
  cm_values: z.string().optional(),
  total_flag: z.boolean().default(false),
  source: z.string().optional(),
});

export const ruleBindSchema = z.object({
  Rule_Id: z.number().int(),
  RuleType: z.number().int(),
  RuleExpression: z.string().optional(),
  RuleExpressionJson: z.string().optional(),
  conditions: z.array(
    z.object({
      Id: z.number(),
      Rule_Id: z.number(),
      Condition_Optr: z.string(),
      Start_Gp: z.string(),
      Data_Type: z.string(),
      Attribute_Name: z.string(),
      Operator: z.string(),
      Condition_Name: z.string(),
      Operand_Datatype: z.string(),
      Value_Type: z.string(),
      Price_Adjustment: z.string().nullable(),
      End_Gp: z.string(),
      Connecter: z.string(),
      Adj_Type: z.string().nullable(),
      Attribute_Type: z.string(),
      Operand_Attribute: z.string().nullable(),
    })
  ).optional(),
  RawRuleJson: z.object({}),
  pricing: z.array(
    z.object({
      Id: z.number(),
      Rule_Id: z.number(),
      Data_Type: z.string(),
      Attribute_Name: z.string(),
      Operator: z.string(),
      Value_Type: z.string().nullable(),
      Expression: z.string(),
      ExpressionJson: z.string(),
      Exp_DataType: z.string(),
      Exp_Attribute: z.string(),
      Exp_Value: z.string().nullable(),
      StartGroup: z.string(),
      EndGroup: z.string().nullable(),
      RootValue: z.string().nullable(),
      RootDataType: z.string().nullable(),
      RootAttribute: z.string().nullable(),
    })
  ).optional(),
});

export const auditEntrySchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  action: z.enum(["CREATE", "UPDATE", "DELETE", "IMPORT", "EXPORT"]),
  entityType: z.enum(["TYPE", "CODE"]),
  entityKey: z.string(),
  oldValue: z.record(z.any()).optional(),
  newValue: z.record(z.any()).optional(),
  user: z.string(),
  note: z.string().optional()
});

export const settingsSchema = z.object({
  canonicalizeCode: z.boolean().default(true),
  enforceUniqueNames: z.boolean().default(true),
  nlpProvider: z.enum(["LocalRules", "NLPS_API", "Proxy"]).default("LocalRules"),
  apiEndpoint: z.string().url().optional(),
  autoSave: z.boolean().default(true),
  theme: z.enum(["light", "dark", "system"]).default("light")
});

export const aiQuerySchema = z.object({
  id: z.string(),
  query: z.string(),
  intent: z.string().optional(),
  entities: z.record(z.any()).optional(),
  confidence: z.number().min(0).max(1).optional(),
  result: z.any().optional(),
  timestamp: z.string().datetime(),
  user: z.string(),
  saved: z.boolean().default(false),
  name: z.string().optional()
});

// Root storage schema
export const storageSchema = z.object({
  types: z.array(typeSchema).default([]),
  rules: z.array(ruleSchema).default([]),
  codes: z.array(codeSchema).default([]),
  audit: z.array(auditEntrySchema).default([]),
  settings: settingsSchema.default({}),
  ai: z.object({
    queries: z.array(aiQuerySchema).default([]),
    savedQueries: z.array(aiQuerySchema).default([])
  }).default({ queries: [], savedQueries: [] })
});


//AI Schema

export const AtomSchema = z.object({
  data_type: z.string(),
  attribute: z.string(),
  operator: z.string(),
  value: z.string(),
});

export const ConditionsSchema = z.object({
  atoms: z.array(AtomSchema),
  connectors: z.array(z.string()),
});

export const ActionSchema = z.object({
  target_data_type: z.string(),
  target_attribute: z.string(),
  literal_value: z.string(),
  expression: z.string(),
});

export const PlaceholdersSchema = z.object({
  data_types: z.array(z.string()),
  attributes: z.array(z.string()),
});

export const PlanSchema = z.object({
  conditions: ConditionsSchema,
  actions: z.array(ActionSchema),
  placeholders: PlaceholdersSchema,
});

export const RuleSchema = z.object({
  id: z.number(),
  name: z.string(),
  approved: z.boolean(),
  status: z.string(),
  created_date: z.string(), // or use z.date() if desired
  data_type_name: z.string(),
  object_type_for: z.string(),
  body: z.string(),
  description: z.string(),
  rule: z.string(),
  rule_expression: z.string(),
  rule_expression_json: z.string(),
});

export const MainSchema = z.object({
  plan: PlanSchema,
  dsl: z.string(),
  session_id: z.string(),
  rules: z.array(RuleSchema),
  rules_count: z.number(),
  message: z.string(),
});

// TypeScript Type
export type MainType = z.infer<typeof MainSchema>;


// Export types
export type Type = z.infer<typeof typeSchema>;
export type Rule = z.infer<typeof ruleSchema>;
export type RuleVersion = z.infer<typeof ruleVersionSchema>;
export type RulePublish = z.infer<typeof rulePublishSchema>;
export type RuleExpression = z.infer<typeof ruleExpressionSchema>;
export type RuleExpressionFilter = z.infer<typeof filteredRuleExpressionSchema>;
export type RuleFilter = z.infer<typeof filteredRuleSchema>;
export type Code = z.infer<typeof codeSchema>;
export type RuleBind = z.infer<typeof ruleBindSchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type Settings = z.infer<typeof settingsSchema>;
export type AIQuery = z.infer<typeof aiQuerySchema>;
export type StorageData = z.infer<typeof storageSchema>;


// Insert schemas
// export const insertRuleSchema = ruleSchema.omit({ ID: true });
// For inserts, code is auto-assigned (numeric per parent group). Allow omitting 'code'.
export const insertCodeSchema = codeSchema
export const insertAuditEntrySchema = auditEntrySchema.omit({ timestamp: true });

export type InsertRule = z.infer<typeof insertRuleSchema>;
export type InsertRuleFunction = z.infer<typeof insertRuleFunctionSchema>;
export type InsertCode = z.infer<typeof insertCodeSchema>;
export type InsertAuditEntry = z.infer<typeof insertAuditEntrySchema>;

export const typeEdgeSchema = z.object({
  fromType: z.string(),
  toType: z.string(),
  sourceType: z.string(),
  targetType: z.string(),
  relation: z.string().optional(),
});

export type TypeEdge = z.infer<typeof typeEdgeSchema>;

export const insertTypeEdgeSchema = typeEdgeSchema.omit({});
export type InsertTypeEdge = z.infer<typeof insertTypeEdgeSchema>;
